import { Instagram, Youtube, Facebook, Twitter } from "lucide-react";
import React, { useEffect, useState } from "react";
import { getFooter } from "../../services/api";

interface FooterData {
  id: string;
  text: string;
  email: string;
  phone_number: string;
  address: string;
  facebook_link: string;
  youtube_link: string;
  instagram_link: string;
  twitter_link: string;
  tiktok_link: string;
}

const Footer: React.FC = () => {
  const [footerData, setFooterData] = useState<FooterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await getFooter();
        if (response.data && response.data.data.length > 0) {
          setFooterData(response.data.data[0]);
        }
      } catch (err) {
        setError("Failed to load footer data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFooterData();
  }, []);

  if (loading)
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#b05d5d]"></div>
      </div>
    );
  if (error)
    return <div className="py-16 text-center text-red-500">{error}</div>;
  if (!footerData)
    return <div className="py-16 text-center">No footer data available</div>;

  return (
    <footer className="bg-[#FBEFEF] lg:bg-white py-16 m-4 lg:m-0">
      <div className="lg:max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
          <div className="space-y-8">
            <div className="lg:text-start text-center">
              <a
                href="/"
                className="flex items-center lg:items-start justify-center lg:justify-start"
              >
                <img src="/images/logo.svg" alt="" />
              </a>
              <h2 className="lg:hidden flex italic text-center flex items-center justify-center">
                We transforms life's moments into masterpieces, blending
                elegance to redefine beauty and confidence.
              </h2>
              <div className="lg:hidden items-center justify-center space-x-8 flex my-3">
                {footerData.instagram_link && (
                  <a
                    href={footerData.instagram_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="/images/instagram.svg"
                      alt="instagram"
                      className="h-6 w-6"
                    />
                  </a>
                )}
                {footerData.youtube_link && (
                  <a
                    href={footerData.youtube_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="/images/youtube.svg"
                      alt="youtube"
                      className="h-6 w-6"
                    />
                  </a>
                )}
                {footerData.tiktok_link && (
                  <a
                    href={footerData.tiktok_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="/images/tiktok.svg"
                      alt="tiktok"
                      className="h-6 w-6"
                    />
                  </a>
                )}
                {footerData.twitter_link && (
                  <a
                    href={footerData.twitter_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Twitter className="h-6 w-6 text-black" />
                  </a>
                )}
                {footerData.facebook_link && (
                  <a
                    href={footerData.facebook_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Facebook className="h-6 w-6 text-blue-600" />
                  </a>
                )}
              </div>
            </div>
            <hr className="block lg:hidden h-[3px] bg-[#B87775]" />
            <div className="flex lg:hidden flex-col lg:flex-row gap-[12px]">
              <h2 className="text-[18px] font-[600]">Quick Links</h2>
              <a
                href="/gallery"
                className="text-[12px] font-[500] hover:text-[#B87775]"
              >
                GALLERY
              </a>
              <a
                href="/reviews"
                className="text-[12px] font-[500] hover:text-[#B87775]"
              >
                REVIEWS
              </a>
              {/* <a
                href="/shop"
                className="text-[12px] font-[500] hover:text-[#B87775]"
              >
                SHOP
              </a> */}
              <a
                href="/our-story"
                className="text-[12px] font-[500] hover:text-[#B87775]"
              >
                OUR STORY
              </a>
              <a
                href="/privacy-policy"
                className="text-[12px] font-[500] hover:text-[#B87775]"
              >
                PRIVACY POLICY
              </a>
            </div>

            <div className="flex lg:hidden flex-col gap-4">
              <h2 className="text-[18px] font-[600]">Contact Us</h2>

              <div>
                <h3 className="text-[12px] font-[500] mb-1">
                  Have questions or suggestions?
                </h3>
                <a
                  href={`mailto:${footerData.email}`}
                  className="text-[12px] text-[#B87775] font-[500] hover:underline"
                >
                  {footerData.email}
                </a>
              </div>

              <div>
                <h3 className="text-[12px] font-[500] mb-1">
                  Need Assistance? Give us a call
                </h3>
                <a
                  href={`tel:${footerData.phone_number}`}
                  className="text-[12px] text-[#B87775] font-[500] hover:underline whitespace-nowrap"
                >
                  {footerData.phone_number}
                </a>
              </div>

              <div>
                <h3 className="text-[12px] font-[500] mb-1">
                  Our Address? Find us here
                </h3>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    footerData.address
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12px] text-[#B87775] font-[500] hover:underline"
                >
                  {footerData.address}
                </a>
              </div>
            </div>

            <div className="flex text-center lg:hidden items-center justify-center">
              <p>©2024 Charmé. All rights reserved</p>
            </div>

            <div className="hidden lg:flex flex-col lg:flex-row items-center gap-8">
              <div>
                <h3 className="font-medium mb-1 text-sm">Email</h3>
                <a
                  href={`mailto:${footerData.email}`}
                  className="text-[18px] text-black font-[500] hover:underline"
                >
                  {footerData.email}
                </a>
              </div>
              <div>
                <h3 className="font-medium mb-1 text-sm">Phone Number</h3>
                <a
                  href={`tel:${footerData.phone_number}`}
                  className="text-[18px] text-black font-[500] whitespace-nowrap hover:underline"
                >
                  {footerData.phone_number}
                </a>
              </div>

              <div>
                <h3 className="font-medium mb-1 text-sm">Address</h3>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    footerData.address
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[18px] text-black font-[500] hover:underline"
                >
                  {footerData.address}
                </a>
              </div>
            </div>
          </div>
          <div className="hidden lg:flex flex-col text-left lg:text-right gap-6">
            <p className="text-[18px] ml-auto lg:w-[320px] font-[500]">
              {footerData.text}
            </p>
            <div className="flex items-center lg:justify-end">
              <a
                href="/booking"
                className="bg-[#B87775] text-white text-[12px] rounded-[8px] flex justify-center items-center hover:bg-[#9a7676] h-[48px] w-[175px]"
              >
                BOOK A SESSION
              </a>
            </div>
          </div>
        </div>
        <div className="hidden lg:flex my-12">
          <hr className="bg-[#00000052] text-[#00000052] h-[2px]" />
        </div>
        <div className="hidden lg:flex flex-col lg:flex-row items-center justify-center lg:justify-between">
          <div className="flex items-center gap-4">
            <p className="text-sm text-[18px] text-black font-[500]">
              Available on Socials
            </p>
            {footerData.instagram_link && (
              <a
                href={footerData.instagram_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-[#B87775]"
              >
                <Instagram className="h-5 w-5" />
              </a>
            )}
            {footerData.youtube_link && (
              <a
                href={footerData.youtube_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-[#B87775]"
              >
                <Youtube className="h-5 w-5" />
              </a>
            )}
            {footerData.facebook_link && (
              <a
                href={footerData.facebook_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-[#B87775]"
              >
                <Facebook className="h-5 w-5" />
              </a>
            )}
            {footerData.twitter_link && (
              <a
                href={footerData.twitter_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-[#B87775]"
              >
                <Twitter className="h-5 w-5" />
              </a>
            )}
            {footerData.tiktok_link && (
              <a
                href={footerData.tiktok_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-[#B87775]"
              >
                <img
                  src="/images/tiktok.svg"
                  alt="tiktok"
                  className="h-5 w-5"
                />
              </a>
            )}
          </div>
          <div className="flex flex-wrap justify-between items-center gap-4">
            <nav className="flex flex-col lg:flex-row space-x-6">
              <a
                href="/gallery"
                className="text-[12px] font-[600] hover:text-[#B87775]"
              >
                GALLERY
              </a>
              <a
                href="/reviews"
                className="text-[12px] font-[600] hover:text-[#B87775]"
              >
                REVIEWS
              </a>
              {/* <a
                href="/shop"
                className="text-[12px] font-[600] hover:text-[#B87775]"
              >
                SHOP
              </a> */}
              <a
                href="/our-story"
                className="text-[12px] font-[600] hover:text-[#B87775]"
              >
                OUR STORY
              </a>
              <a
                href="/privacy-policy"
                className="text-[12px] font-[600] hover:text-[#B87775]"
              >
                PRIVACY POLICY
              </a>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
